import TimerTemplatePreview from '@components/templates/timerTemplatePreview';
import { Button, Label, Modal, Radio, RadioGroup } from '@heroui/react';
import { COUNTDOWN_POSITION_OPTIONS, TIMER_TEMPLATES, TimerTemplateId } from '@lib/timerTemplates';
import { useEffect, useState } from 'react';
import { CountdownPosition } from '@/types';

type TemplateSelectionModalProps = {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	onSelect: (templateId: TimerTemplateId, countdownPosition: CountdownPosition) => void;
};

const DEFAULT_MODAL_COUNTDOWN_POSITION = COUNTDOWN_POSITION_OPTIONS[0].value;

export default function TemplateSelectionModal({ isOpen, onOpenChange, onSelect }: TemplateSelectionModalProps) {
	const [selectedTemplateId, setSelectedTemplateId] = useState<TimerTemplateId | ''>('');

	useEffect(() => {
		if (isOpen) {
			setSelectedTemplateId('');
		}
	}, [isOpen]);

	const handleSelectPress = () => {
		if (!selectedTemplateId) {
			return;
		}

		onSelect(selectedTemplateId, DEFAULT_MODAL_COUNTDOWN_POSITION);
	};

	return (
		<Modal>
			<Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange} variant='blur'>
				<Modal.Container size='cover' scroll='inside' className='max-h-[90vh]'>
					<Modal.Dialog className='max-md:p-4'>
						<Modal.CloseTrigger onPress={() => onOpenChange(false)} />
						<Modal.Header className='flex flex-col gap-1'>
							<Modal.Heading>Select template</Modal.Heading>
						</Modal.Header>
						<Modal.Body className='border-y border-[#ECEFF3] p-4 max-md:p-2'>
							<RadioGroup
								aria-label='Timer template'
								value={selectedTemplateId}
								onChange={(value) => setSelectedTemplateId(value as TimerTemplateId)}
							>
								<div className='grid grid-cols-1 gap-3'>
									{TIMER_TEMPLATES.map((template) => (
										<Radio key={template.id} value={template.id} className='group mt-0! min-w-0'>
											<Radio.Content className='relative block min-h-[146px] w-full cursor-pointer max-md:min-h-[112px] overflow-hidden rounded-[22px] border border-transparent bg-white text-left transition hover:border-[#116DFF] hover:shadow-sm group-data-[selected=true]:border-[#116DFF] data-[focus-visible=true]:outline data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-[#116DFF]'>
												<Radio.Control className='absolute left-4 top-4 z-10 size-8 overflow-hidden rounded-full border-[#D0D7DF] bg-white max-md:left-2 max-md:top-2 max-md:size-6'>
													<Radio.Indicator className='rounded-full bg-white'>
														{({ isSelected }) => (
															<span
																className={`size-3 rounded-full max-md:size-2.5 ${
																	isSelected ? 'bg-[#116DFF]' : 'bg-transparent'
																}`}
															/>
														)}
													</Radio.Indicator>
												</Radio.Control>
												<TimerTemplatePreview
													template={template}
													countdownPosition={DEFAULT_MODAL_COUNTDOWN_POSITION}
													isWide
												/>
												<Label className='sr-only'>{template.name}</Label>
											</Radio.Content>
										</Radio>
									))}
								</div>
							</RadioGroup>
						</Modal.Body>
						<Modal.Footer>
							<Button variant='primary' onPress={handleSelectPress}>
								Select
							</Button>
						</Modal.Footer>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
